import { styled } from "goober";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { INTERCOM_ROUTE } from "../../shared/reservedRoutes";
import {
  intercomIframeSandbox,
  type IntercomBootMessage,
  type IntercomNavigateMessage,
  type IntercomShowMessage,
  type IntercomThemeMessage,
  type IntercomUnreadMessage,
  type IntercomVisibilityMessage,
} from "../../shared/intercomShell";
import { publicUrl } from "../lib/tenantOrigin";
import { useCurrentLocation } from "../lib/urls";
import { zIndexes } from "../lib/zIndexes";
import { intercomUserHash } from "./useSessionBootstrap";

const Frame = styled("iframe")`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  background: transparent;
  z-index: ${zIndexes.intercomMessenger};
`;

type Props = {
  user: { id: string; email: string; name: string };
  open: boolean;
  onClose: () => void;
  onUnreadChange: (count: number) => void;
};

function displayedColorScheme(): "light" | "dark" {
  const scheme = document.documentElement.style.colorScheme;
  if (scheme === "light" || scheme === "dark") return scheme;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

// The frame boots hidden at sign-in and stays mounted, so replies from
// support arrive (as unread-count messages) even while the messenger is
// closed, and the conversation survives close/reopen.
export function IntercomMessenger({
  user,
  open,
  onClose,
  onUnreadChange,
}: Props) {
  const [visible, setVisible] = useState(false);
  const booted = useRef(false);
  const openRef = useRef(open);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const currentLocation = useCurrentLocation();
  const locationRef = useRef(currentLocation);
  const shellUrl = useMemo(() => `${publicUrl()}${INTERCOM_ROUTE}`, []);
  const shellOrigin = useMemo(() => new URL(shellUrl).origin, [shellUrl]);

  const postShow = useCallback(() => {
    const message: IntercomShowMessage = { type: "3b-intercom-show" };
    frameRef.current?.contentWindow?.postMessage(message, shellOrigin);
  }, [shellOrigin]);

  useEffect(() => {
    openRef.current = open;
    if (open && booted.current) postShow();
  }, [open, postShow]);

  useEffect(() => {
    locationRef.current = currentLocation;
    if (!booted.current) return;
    const message: IntercomNavigateMessage = {
      type: "3b-intercom-navigate",
      path: currentLocation,
    };
    frameRef.current?.contentWindow?.postMessage(message, shellOrigin);
  }, [currentLocation, shellOrigin]);

  useEffect(() => {
    let lastSent = displayedColorScheme();
    const observer = new MutationObserver(() => {
      const scheme = displayedColorScheme();
      if (scheme === lastSent || !booted.current) return;
      lastSent = scheme;
      const message: IntercomThemeMessage = {
        type: "3b-intercom-theme",
        colorScheme: scheme,
      };
      frameRef.current?.contentWindow?.postMessage(message, shellOrigin);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    });
    return () => observer.disconnect();
  }, [shellOrigin]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== shellOrigin) return;
      if (
        !frameRef.current ||
        event.source !== frameRef.current.contentWindow
      ) {
        return;
      }
      const data = event.data as
        | IntercomVisibilityMessage
        | IntercomUnreadMessage
        | undefined;
      if (data?.type === "3b-intercom-visibility") {
        const isVisible = data.visible === true;
        setVisible(isVisible);
        if (!isVisible) onClose();
      } else if (
        data?.type === "3b-intercom-unread" &&
        typeof data.count === "number"
      ) {
        onUnreadChange(data.count);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [shellOrigin, onClose, onUnreadChange]);

  const boot = useCallback(() => {
    const message: IntercomBootMessage = {
      type: "3b-intercom-boot",
      userId: user.id,
      email: user.email,
      name: user.name,
      userHash: intercomUserHash(),
      path: locationRef.current,
      colorScheme: displayedColorScheme(),
    };
    frameRef.current?.contentWindow?.postMessage(message, shellOrigin);
    booted.current = true;
    if (openRef.current) postShow();
  }, [user.id, user.email, user.name, shellOrigin, postShow]);

  return (
    <Frame
      ref={frameRef}
      src={shellUrl}
      title="Support"
      sandbox={intercomIframeSandbox}
      onLoad={boot}
      style={
        visible ? undefined : { visibility: "hidden", pointerEvents: "none" }
      }
    />
  );
}
